import * as THREE from "three/webgpu";
import {color, shapeCircle, texture, vec4} from "three/tsl";
import {CameraSpriteNodeMaterial} from "./CameraSpriteNodeMaterial";
import { MEASURE_SELECTED_COLOR } from './MeasureSelectionMaterialFactory';
import type { FeaturePointMaterialState } from '../../types';

/** 草图点默认贴图地址。 */
const POINT_DEFAULT_TEXTURE_URL = '/textures/sketch/point-default.svg';
/** 圆、圆弧和椭圆中心点十字贴图地址。 */
const CURVE_CENTER_POINT_TEXTURE_URL = '/textures/sketch/point-curve-center.svg';

/** 线控制点默认颜色（紫色）。 */
const SKETCH_CONTROL_POINT_COLOR = 0x8a2be2;
/** 线控制点悬停色（橙色）。 */
const SKETCH_CONTROL_POINT_HOVER_COLOR = 0xffa500;
/** 线控制点选中色（蓝色）。 */
const SKETCH_CONTROL_POINT_SELECTED_COLOR = 0x53a9ff;
/** 草图点默认色（与默认控制点保持一致）。 */
const SKETCH_POINT_DEFAULT_COLOR = SKETCH_CONTROL_POINT_COLOR;
/** 草图点纹理遮罩颜色（白色，便于材质节点统一着色）。 */
const SKETCH_POINT_TEXTURE_MASK_COLOR = 0xffffff;
/** 草图点创建色（亮蓝）。 */
const SKETCH_POINT_CREATED_COLOR = 0x3b82f6;
/** 草图点悬停色（橙色）。 */
const SKETCH_POINT_HOVER_COLOR = 0xff8000;
/** 草图点选中色（蓝色）。 */
const SKETCH_POINT_SELECTED_COLOR = 0x53a9ff;
/** 草图会话原点投影颜色（红色）。 */
const DRAWING_SKETCH_ORIGIN_COLOR = 0xff0000;
/** 草图会话原点投影悬停颜色（亮红）。 */
const DRAWING_SKETCH_ORIGIN_HOVER_COLOR = 0xff6666;
/** 草图会话原点投影选中颜色（深红）。 */
const DRAWING_SKETCH_ORIGIN_SELECTED_COLOR = 0xcc0000;

/** 绘制期草图参考点颜色（绿色）。 */
const DRAWING_SKETCH_REFERENCE_POINT_COLOR = 0x16a34a;
/** 绘制期草图参考点悬停颜色（亮绿色）。 */
const DRAWING_SKETCH_REFERENCE_POINT_HOVER_COLOR = 0x22c55e;
/** 绘制期草图参考点选中颜色（深绿色）。 */
const DRAWING_SKETCH_REFERENCE_POINT_SELECTED_COLOR = 0x15803d;

/** 基准点默认色（与控制点默认态保持一致）。 */
const DATUM_POINT_DEFAULT_COLOR = SKETCH_CONTROL_POINT_COLOR;
/** 基准点悬停色（与控制点悬停态保持一致）。 */
const DATUM_POINT_HOVER_COLOR = SKETCH_CONTROL_POINT_HOVER_COLOR;
/** 基准点选中色（与控制点选中态保持一致）。 */
const DATUM_POINT_SELECTED_COLOR = SKETCH_CONTROL_POINT_SELECTED_COLOR;
/** 固定点默认色（黑色）。 */
const SKETCH_FIXED_POINT_COLOR = 0x000000;
/** 特征点默认色（黑色）。 */
const FEATURE_POINT_COLOR_DEFAULT = 0x000000;
/** 特征点悬停色（橙色）。 */
const FEATURE_POINT_COLOR_HOVER = 0xff8000;
/** 特征点选中色（蓝色）。 */
const FEATURE_POINT_COLOR_SELECTED = 0x53a9ff;
/** 测量锚点颜色（白色）。 */
const MEASURE_ANCHOR_COLOR = 0xffffff;
/** 非活动默认态颜色。 */
const INACTIVE_POINT_COLOR = 0xd0d4d8;
/** 非活动默认态透明度。 */
const INACTIVE_POINT_OPACITY = 0.42;
/** 椭圆轴端控制点非活动态雪花材质名称。 */
const ELLIPSE_AXIS_CONTROL_POINT_INACTIVE_MATERIAL_NAME = '椭圆轴端控制点-非活动雪花材质';
/** 曲线中心点默认态十字材质名称。 */
const CURVE_CENTER_POINT_DEFAULT_MATERIAL_NAME = '曲线中心点-默认态十字材质';
/** 曲线中心点移入态十字材质名称。 */
const CURVE_CENTER_POINT_HOVER_MATERIAL_NAME = '曲线中心点-移入态十字材质';
/** 曲线中心点选中态十字材质名称。 */
const CURVE_CENTER_POINT_SELECTED_MATERIAL_NAME = '曲线中心点-选中态十字材质';
/** 曲线中心点固定态十字材质名称。 */
const CURVE_CENTER_POINT_FIXED_MATERIAL_NAME = '曲线中心点-固定态十字材质';
/** 曲线中心点非活动态十字材质名称。 */
const CURVE_CENTER_POINT_INACTIVE_MATERIAL_NAME = '曲线中心点-非活动态十字材质';
/** 曲线中心点固定非活动态十字材质名称。 */
const CURVE_CENTER_POINT_FIXED_INACTIVE_MATERIAL_NAME = '曲线中心点-固定非活动态十字材质';
/** 曲线中心点默认颜色（与草图点雪花默认色一致）。 */
const CURVE_CENTER_POINT_DEFAULT_COLOR = SKETCH_POINT_DEFAULT_COLOR;
/** 实体引用预览点颜色。 */
const REFERENCE_PREVIEW_POINT_COLOR = 0x53a9ff;
/** 实体引用来源高亮点颜色。 */
const REFERENCE_SOURCE_HIGHLIGHT_POINT_COLOR = 0xe0b400;
/**
 * 点精灵材质名称枚举。
 */
enum PointSpriteMaterialName {
    /** 创建态点材质名称。 */
    SketchPointCreated = '草图点-创建态材质',
    /** 默认态点材质名称。 */
    SketchPointDefault = '草图点-默认态材质',
    /** 非活动默认态点材质名称。 */
    SketchPointInactiveDefault = '草图点-非活动默认态材质',
    /** 固定态点材质名称。 */
    SketchPointFixedDefault = '草图点-固定默认态材质',
    /** 固定态非活动默认材质名称。 */
    SketchPointFixedInactiveDefault = '草图点-固定非活动默认态材质',
    /** 悬停态点材质名称。 */
    SketchPointHover = '草图点-悬停态材质',
    /** 选中态点材质名称。 */
    SketchPointSelected = '草图点-选中态材质',
    /** 控制点材质名称。 */
    SketchControlPoint = '草图点-控制点材质',
    /** 控制点非活动默认态材质名称。 */
    SketchControlPointInactive = '草图点-控制点非活动默认态材质',
    /** 固定态控制点材质名称。 */
    SketchControlPointFixed = '草图点-控制点固定态材质',
    /** 固定态控制点非活动默认材质名称。 */
    SketchControlPointFixedInactive = '草图点-控制点固定非活动态材质',
    /** 控制点悬停态材质名称。 */
    SketchControlPointHover = '草图点-控制点悬停态材质',
    /** 控制点选中态材质名称。 */
    SketchControlPointSelected = '草图点-控制点选中态材质',
    /** 实体引用预览点材质名称。 */
    SketchPointReferencePreview = '草图点-实体引用预览态材质',
    /** 实体引用来源高亮点材质名称。 */
    SketchPointReferenceSourceHighlight = '草图点-实体引用来源高亮态材质',
    /** 实体引用预览控制点材质名称。 */
    SketchControlPointReferencePreview = '草图点-控制点实体引用预览态材质',
    /** 实体引用来源高亮控制点材质名称。 */
    SketchControlPointReferenceSourceHighlight = '草图点-控制点实体引用来源高亮态材质',
    /** 基准点默认态材质名称。 */
    DatumPointDefault = '基准点-默认态材质',
    /** 基准点悬停态材质名称。 */
    DatumPointHover = '基准点-悬停态材质',
    /** 基准点选中态材质名称。 */
    DatumPointSelected = '基准点-选中态材质',
    /** 测量锚点材质名称。 */
    MeasureAnchor = '测量-锚点材质',
    /** 测量选中点材质名称。 */
    MeasureSelectedPoint = '测量-选中点材质',
}


/**
 * 草图点复制材质兜底类型枚举。
 * 说明：
 * 1. 点复制时优先按源材质名称恢复规范材质；
 * 2. 源材质名称缺失或来自旧数据时，使用该兜底类型决定默认点材质；
 * 3. 该枚举只表达材质兜底语义，不直接依赖草图点对象，避免材质层反向依赖 mesh 层。
 */
export enum SketchPointCloneMaterialFallbackKind {
    /** 控制点兜底为紫色控制点材质。 */
    ControlPoint = 'controlPoint',
    /** 普通草图点兜底为草图点默认贴图材质。 */
    SketchPoint = 'sketchPoint',
}


/**
 * 点材质工厂。
 */
export class PointsMaterialFactory {
    static sketchPointSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointInactiveDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointFixedDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointFixedInactiveDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointReferencePreviewSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointReferenceSourceHighlightSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointInactiveSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointFixedSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointFixedInactiveSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    /** 椭圆轴端控制点非活动态雪花材质缓存。 */
    static ellipseAxisControlPointInactiveSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点默认态十字材质缓存。 */
    static curveCenterPointSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点移入态十字材质缓存。 */
    static curveCenterPointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点选中态十字材质缓存。 */
    static curveCenterPointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点固定态十字材质缓存。 */
    static curveCenterPointFixedSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点非活动态十字材质缓存。 */
    static curveCenterPointInactiveSpriteMaterial: CameraSpriteNodeMaterial;
    /** 曲线中心点固定非活动态十字材质缓存。 */
    static curveCenterPointFixedInactiveSpriteMaterial: CameraSpriteNodeMaterial;
    static drawingSketchOriginSpriteMaterial: CameraSpriteNodeMaterial;
    static drawingSketchOriginHoverSpriteMaterial: CameraSpriteNodeMaterial;
    static drawingSketchOriginSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    
    /** 绘制期草图参考点默认态材质缓存。 */
    static drawingSketchReferencePointSpriteMaterial: CameraSpriteNodeMaterial;
    /** 绘制期草图参考点悬停态材质缓存。 */
    static drawingSketchReferencePointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    /** 绘制期草图参考点选中态材质缓存。 */
    static drawingSketchReferencePointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    
    static sketchControlPointReferencePreviewSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchControlPointReferenceSourceHighlightSpriteMaterial: CameraSpriteNodeMaterial;
    static datumPointDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static datumPointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    static datumPointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    static featurePointDefaultSpriteMaterial: CameraSpriteNodeMaterial;
    static featurePointHoverSpriteMaterial: CameraSpriteNodeMaterial;
    static featurePointSelectedSpriteMaterial: CameraSpriteNodeMaterial;
    static measureAnchorSpriteMaterial: CameraSpriteNodeMaterial;
    static measureSelectedPointSpriteMaterial: CameraSpriteNodeMaterial;
    static sketchPointDefaultTexture: THREE.Texture;
    /** 曲线中心点十字 SVG 纹理缓存。 */
    static curveCenterPointTexture: THREE.Texture;

    /**
     * 清空点材质缓存。
     * 说明：
     * 1. InteractionStateManager 会在模式切换时统一请求清空状态材质缓存；
     * 2. development 分支已有该生命周期入口，rebase 时被误丢；
     * 3. 这里按当前 Sprite 点链统一回收所有缓存引用，避免交互状态继续复用旧材质。
     */
    static clearCache(): void {
        this.sketchPointSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointInactiveDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointFixedDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointFixedInactiveDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointReferencePreviewSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchPointReferenceSourceHighlightSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointInactiveSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointFixedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointFixedInactiveSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.ellipseAxisControlPointInactiveSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointFixedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointInactiveSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.curveCenterPointFixedInactiveSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.drawingSketchOriginSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.drawingSketchOriginHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.drawingSketchOriginSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        
        // 清理绿色参考点材质缓存，确保模式切换后不会复用旧状态材质。
        this.drawingSketchReferencePointSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.drawingSketchReferencePointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.drawingSketchReferencePointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        
        this.sketchControlPointReferencePreviewSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.sketchControlPointReferenceSourceHighlightSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.datumPointDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.datumPointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.datumPointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.featurePointDefaultSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.featurePointHoverSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.featurePointSelectedSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.measureAnchorSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
        this.measureSelectedPointSpriteMaterial = undefined as unknown as CameraSpriteNodeMaterial;
    }

    
    /**
     * 解析草图点复制时应使用的规范材质。
     *
     * 设计说明：
     * 1. Three TSL 节点材质直接 `clone()` 会丢失节点配置，复制实体时会退化成大号纯白点；
     * 2. 草图点复制只需要继承材质语义，不需要独占材质实例，因此应回到工厂缓存的规范材质；
     * 3. 源材质名称来自旧版本或空字符串时，按点类型返回稳定兜底材质，避免复制流程中断。
     *
     * @param materialName 源点材质名称。
     * @param fallbackKind 源材质名称无法识别时使用的点类型兜底。
     * @returns 可直接用于草图点副本的规范点精灵材质。
     */
    static resolveSketchPointCloneMaterial(
        materialName: string,
        fallbackKind: SketchPointCloneMaterialFallbackKind,
    ): CameraSpriteNodeMaterial {
        switch (materialName) {
            case PointSpriteMaterialName.SketchPointCreated:
                return PointsMaterialFactory.getSketchPointSpriteMaterial();
            case PointSpriteMaterialName.SketchPointDefault:
                return PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
            case PointSpriteMaterialName.SketchPointInactiveDefault:
                return PointsMaterialFactory.getSketchPointInactiveDefaultSpriteMaterial();
            case PointSpriteMaterialName.SketchPointFixedDefault:
                return PointsMaterialFactory.getSketchPointFixedDefaultSpriteMaterial();
            case PointSpriteMaterialName.SketchPointFixedInactiveDefault:
                return PointsMaterialFactory.getSketchPointFixedInactiveDefaultSpriteMaterial();
            case PointSpriteMaterialName.SketchPointHover:
                return PointsMaterialFactory.getSketchPointHoverSpriteMaterial();
            case PointSpriteMaterialName.SketchPointSelected:
                return PointsMaterialFactory.getSketchPointSelectedSpriteMaterial();
            case PointSpriteMaterialName.SketchPointReferencePreview:
                return PointsMaterialFactory.getReferencePreviewMaterial();
            case PointSpriteMaterialName.SketchPointReferenceSourceHighlight:
                return PointsMaterialFactory.getReferenceSourceHighlightMaterial();
            case PointSpriteMaterialName.SketchControlPoint:
                return PointsMaterialFactory.getSketchControlPointSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointInactive:
                return PointsMaterialFactory.getSketchControlPointInactiveSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointFixed:
                return PointsMaterialFactory.getSketchControlPointFixedSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointFixedInactive:
                return PointsMaterialFactory.getSketchControlPointFixedInactiveSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointHover:
                return PointsMaterialFactory.getSketchControlPointHoverSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointSelected:
                return PointsMaterialFactory.getSketchControlPointSelectedSpriteMaterial();
            case ELLIPSE_AXIS_CONTROL_POINT_INACTIVE_MATERIAL_NAME:
                return PointsMaterialFactory.getEllipseAxisControlPointInactiveSpriteMaterial();
            case CURVE_CENTER_POINT_DEFAULT_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointSpriteMaterial();
            case CURVE_CENTER_POINT_HOVER_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointHoverSpriteMaterial();
            case CURVE_CENTER_POINT_SELECTED_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointSelectedSpriteMaterial();
            case CURVE_CENTER_POINT_FIXED_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointFixedSpriteMaterial();
            case CURVE_CENTER_POINT_INACTIVE_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointInactiveSpriteMaterial();
            case CURVE_CENTER_POINT_FIXED_INACTIVE_MATERIAL_NAME:
                return PointsMaterialFactory.getCurveCenterPointFixedInactiveSpriteMaterial();
            case PointSpriteMaterialName.SketchControlPointReferencePreview:
                return PointsMaterialFactory.getReferencePreviewControlPointMaterial();
            case PointSpriteMaterialName.SketchControlPointReferenceSourceHighlight:
                return PointsMaterialFactory.getReferenceSourceHighlightControlPointMaterial();
            default:
                return PointsMaterialFactory.resolveSketchPointCloneFallbackMaterial(fallbackKind);
        }
    }

    /**
     * 按点复制兜底类型解析默认材质。
     *
     * @param fallbackKind 点复制材质兜底类型。
     * @returns 规范点精灵材质。
     */
    private static resolveSketchPointCloneFallbackMaterial(
        fallbackKind: SketchPointCloneMaterialFallbackKind,
    ): CameraSpriteNodeMaterial {
        switch (fallbackKind) {
            case SketchPointCloneMaterialFallbackKind.ControlPoint:
                return PointsMaterialFactory.getSketchControlPointSpriteMaterial();
            case SketchPointCloneMaterialFallbackKind.SketchPoint:
                return PointsMaterialFactory.getSketchPointDefaultSpriteMaterial();
            default:
                throw new Error(`未支持的草图点复制材质兜底类型：${fallbackKind as string}`);
        }
    }
    

    /**
     * 草图点创建样式。
     * @returns 创建态点精灵材质。
     */
    static getSketchPointSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointSpriteMaterial) {
            return PointsMaterialFactory.sketchPointSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointSpriteMaterial = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointCreated,
        );
        return PointsMaterialFactory.sketchPointSpriteMaterial;
    }

    /**
     * 草图点默认样式。
     * @returns 默认状态点精灵材质。
     */
    static getSketchPointDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointDefaultSpriteMaterial) {
            return PointsMaterialFactory.sketchPointDefaultSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointDefaultSpriteMaterial = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointDefault,
        );
        return PointsMaterialFactory.sketchPointDefaultSpriteMaterial;
    }

    /**
     * 获取非活动默认态点材质。
     *
     * @returns 非活动默认态点精灵材质。
     */
    static getInactiveMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial) {
            return PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            INACTIVE_POINT_COLOR,
        );
        PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial.opacity = INACTIVE_POINT_OPACITY;
        PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial.name = PointSpriteMaterialName.SketchPointInactiveDefault;
        return PointsMaterialFactory.sketchPointInactiveDefaultSpriteMaterial;
    }

    /**
     * 获取草图点非活动默认样式。
     *
     * @returns 非活动默认态点精灵材质。
     */
    static getSketchPointInactiveDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        return PointsMaterialFactory.getInactiveMaterial();
    }

    /**
     * 草图点固定默认样式。
     * @returns 固定默认状态点精灵材质。
     */
    static getSketchPointFixedDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointFixedDefaultSpriteMaterial) {
            return PointsMaterialFactory.sketchPointFixedDefaultSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointFixedDefaultSpriteMaterial = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointFixedDefault,
        );
        return PointsMaterialFactory.sketchPointFixedDefaultSpriteMaterial;
    }

    /**
     * 获取草图点固定非活动默认样式。
     *
     * @returns 固定非活动默认态点精灵材质。
     */
    static getSketchPointFixedInactiveDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        return PointsMaterialFactory.getInactiveMaterial();
    }

    /**
     * 草图点悬停样式。
     * @returns 悬停状态点精灵材质。
     */
    static getSketchPointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointHoverSpriteMaterial) {
            return PointsMaterialFactory.sketchPointHoverSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointHoverSpriteMaterial = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointHover,
        );
        return PointsMaterialFactory.sketchPointHoverSpriteMaterial;
    }

    /**
     * 草图点选中样式。
     * @returns 选中状态点精灵材质。
     */
    static getSketchPointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointSelectedSpriteMaterial) {
            return PointsMaterialFactory.sketchPointSelectedSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointSelectedSpriteMaterial = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointSelected,
        );
        return PointsMaterialFactory.sketchPointSelectedSpriteMaterial;
    }

    /**
     * 获取实体引用预览点材质。
     *
     * @returns 实体引用预览点精灵材质。
     */
    static getReferencePreviewMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointReferencePreviewSpriteMaterial) {
            return PointsMaterialFactory.sketchPointReferencePreviewSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointReferencePreviewSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            REFERENCE_PREVIEW_POINT_COLOR,
        );
        PointsMaterialFactory.sketchPointReferencePreviewSpriteMaterial.name = PointSpriteMaterialName.SketchPointReferencePreview;
        return PointsMaterialFactory.sketchPointReferencePreviewSpriteMaterial;
    }

    /**
     * 获取实体引用来源高亮点材质。
     *
     * @returns 实体引用来源高亮点精灵材质。
     */
    static getReferenceSourceHighlightMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchPointReferenceSourceHighlightSpriteMaterial) {
            return PointsMaterialFactory.sketchPointReferenceSourceHighlightSpriteMaterial;
        }
        PointsMaterialFactory.sketchPointReferenceSourceHighlightSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            REFERENCE_SOURCE_HIGHLIGHT_POINT_COLOR,
        );
        PointsMaterialFactory.sketchPointReferenceSourceHighlightSpriteMaterial.name = PointSpriteMaterialName.SketchPointReferenceSourceHighlight;
        return PointsMaterialFactory.sketchPointReferenceSourceHighlightSpriteMaterial;
    }

    /**
     * 控制点样式。
     * @returns 控制点精灵材质。
     */
    static getSketchControlPointSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointSpriteMaterial;
        }
        PointsMaterialFactory.sketchControlPointSpriteMaterial = PointsMaterialFactory.createControlPointSpriteMaterial();
        return PointsMaterialFactory.sketchControlPointSpriteMaterial;
    }

    /**
     * 获取控制点非活动默认样式。
     *
     * @returns 控制点非活动默认精灵材质。
     */
    static getSketchControlPointInactiveSpriteMaterial(): CameraSpriteNodeMaterial {
        return PointsMaterialFactory.getInactiveMaterial();
    }

    /**
     * 获取椭圆轴端控制点的非活动态雪花材质。
     *
     * 该材质复用草图点雪花遮罩，只调整为非活动透明度；普通控制点仍使用圆形非活动材质。
     *
     * @returns 椭圆轴端控制点非活动态雪花材质。
     */
    static getEllipseAxisControlPointInactiveSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.ellipseAxisControlPointInactiveSpriteMaterial) {
            return PointsMaterialFactory.ellipseAxisControlPointInactiveSpriteMaterial;
        }
        const material = PointsMaterialFactory.createSketchPointSpriteMaterial(
            PointSpriteMaterialName.SketchPointInactiveDefault,
        );
        material.name = ELLIPSE_AXIS_CONTROL_POINT_INACTIVE_MATERIAL_NAME;
        material.opacity = INACTIVE_POINT_OPACITY;
        PointsMaterialFactory.ellipseAxisControlPointInactiveSpriteMaterial = material;
        return material;
    }

    /**
     * 获取圆、圆弧和椭圆中心点默认态十字材质。
     * @returns 默认态紫色十字材质。
     */
    static getCurveCenterPointSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_DEFAULT_MATERIAL_NAME,
                CURVE_CENTER_POINT_DEFAULT_COLOR,
            );
        }
        return PointsMaterialFactory.curveCenterPointSpriteMaterial;
    }

    /**
     * 获取圆、圆弧和椭圆中心点移入态十字材质。
     * @returns 移入态橙色十字材质。
     */
    static getCurveCenterPointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointHoverSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointHoverSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_HOVER_MATERIAL_NAME,
                SKETCH_POINT_HOVER_COLOR,
            );
        }
        return PointsMaterialFactory.curveCenterPointHoverSpriteMaterial;
    }

    /**
     * 获取圆、圆弧和椭圆中心点选中态十字材质。
     * @returns 选中态蓝色十字材质。
     */
    static getCurveCenterPointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointSelectedSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointSelectedSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_SELECTED_MATERIAL_NAME,
                SKETCH_POINT_SELECTED_COLOR,
            );
        }
        return PointsMaterialFactory.curveCenterPointSelectedSpriteMaterial;
    }

    /**
     * 获取圆、圆弧和椭圆中心点固定态十字材质。
     * @returns 固定态黑色十字材质。
     */
    static getCurveCenterPointFixedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointFixedSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointFixedSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_FIXED_MATERIAL_NAME,
                SKETCH_FIXED_POINT_COLOR,
            );
        }
        return PointsMaterialFactory.curveCenterPointFixedSpriteMaterial;
    }

    /**
     * 获取圆、圆弧和椭圆中心点非活动态十字材质。
     * @returns 非活动态半透明十字材质。
     */
    static getCurveCenterPointInactiveSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointInactiveSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointInactiveSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_INACTIVE_MATERIAL_NAME,
                INACTIVE_POINT_COLOR,
                INACTIVE_POINT_OPACITY,
            );
        }
        return PointsMaterialFactory.curveCenterPointInactiveSpriteMaterial;
    }

    /**
     * 获取圆、圆弧和椭圆中心点固定非活动态十字材质。
     * @returns 固定非活动态半透明灰色十字材质。
     */
    static getCurveCenterPointFixedInactiveSpriteMaterial(): CameraSpriteNodeMaterial {
        if (!PointsMaterialFactory.curveCenterPointFixedInactiveSpriteMaterial) {
            PointsMaterialFactory.curveCenterPointFixedInactiveSpriteMaterial = PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
                CURVE_CENTER_POINT_FIXED_INACTIVE_MATERIAL_NAME,
                INACTIVE_POINT_COLOR,
                INACTIVE_POINT_OPACITY,
            );
        }
        return PointsMaterialFactory.curveCenterPointFixedInactiveSpriteMaterial;
    }

    /**
     * 控制点固定默认样式。
     * @returns 控制点固定默认精灵材质。
     */
    static getSketchControlPointFixedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointFixedSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointFixedSpriteMaterial;
        }
        PointsMaterialFactory.sketchControlPointFixedSpriteMaterial = PointsMaterialFactory.createControlPointStateSpriteMaterial(
            PointSpriteMaterialName.SketchControlPointFixed,
            SKETCH_FIXED_POINT_COLOR,
        );
        return PointsMaterialFactory.sketchControlPointFixedSpriteMaterial;
    }

    /**
     * 获取固定控制点非活动默认样式。
     *
     * @returns 固定控制点非活动精灵材质。
     */
    static getSketchControlPointFixedInactiveSpriteMaterial(): CameraSpriteNodeMaterial {
        return PointsMaterialFactory.getInactiveMaterial();
    }

    /**
     * 控制点悬停样式。
     * @returns 控制点悬停精灵材质。
     */
    static getSketchControlPointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointHoverSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointHoverSpriteMaterial;
        }
        PointsMaterialFactory.sketchControlPointHoverSpriteMaterial = PointsMaterialFactory.createControlPointStateSpriteMaterial(
            PointSpriteMaterialName.SketchControlPointHover,
            SKETCH_CONTROL_POINT_HOVER_COLOR,
        );
        return PointsMaterialFactory.sketchControlPointHoverSpriteMaterial;
    }

    /**
     * 控制点选中样式。
     * @returns 控制点选中精灵材质。
     */
    static getSketchControlPointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointSelectedSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointSelectedSpriteMaterial;
        }
        PointsMaterialFactory.sketchControlPointSelectedSpriteMaterial = PointsMaterialFactory.createControlPointStateSpriteMaterial(
            PointSpriteMaterialName.SketchControlPointSelected,
            SKETCH_CONTROL_POINT_SELECTED_COLOR,
        );
        return PointsMaterialFactory.sketchControlPointSelectedSpriteMaterial;
    }

    /**
     * 获取草图会话原点投影材质。
     * @returns 红色草图会话原点投影精灵材质。
     */
    static getDrawingSketchOriginSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchOriginSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchOriginSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchOriginSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_ORIGIN_COLOR,
        );
        PointsMaterialFactory.drawingSketchOriginSpriteMaterial.name = '草图会话原点投影-默认态材质';
        PointsMaterialFactory.drawingSketchOriginSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchOriginSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchOriginSpriteMaterial;
    }

    /**
     * 获取草图会话原点投影悬停材质。
     * @returns 红色草图会话原点投影悬停精灵材质。
     */
    static getDrawingSketchOriginHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_ORIGIN_HOVER_COLOR,
        );
        PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial.name = '草图会话原点投影-悬停态材质';
        PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchOriginHoverSpriteMaterial;
    }

    /**
     * 获取草图会话原点投影选中材质。
     * @returns 红色草图会话原点投影选中精灵材质。
     */
    static getDrawingSketchOriginSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_ORIGIN_SELECTED_COLOR,
        );
        PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial.name = '草图会话原点投影-选中态材质';
        PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchOriginSelectedSpriteMaterial;
    }

    
    /**
     * 获取绘制期草图参考点材质。
     * @returns 绿色绘制期草图参考点精灵材质。
     */
    static getDrawingSketchReferencePointSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_REFERENCE_POINT_COLOR,
        );
        PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial.name = '绘制期草图参考点-默认态材质';
        PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchReferencePointSpriteMaterial;
    }

    /**
     * 获取绘制期草图参考点悬停材质。
     * @returns 绿色绘制期草图参考点悬停精灵材质。
     */
    static getDrawingSketchReferencePointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_REFERENCE_POINT_HOVER_COLOR,
        );
        PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial.name = '绘制期草图参考点-悬停态材质';
        PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchReferencePointHoverSpriteMaterial;
    }

    /**
     * 获取绘制期草图参考点选中材质。
     * @returns 绿色绘制期草图参考点选中精灵材质。
     */
    static getDrawingSketchReferencePointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial) {
            return PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial;
        }
        PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DRAWING_SKETCH_REFERENCE_POINT_SELECTED_COLOR,
        );
        PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial.name = '绘制期草图参考点-选中态材质';
        PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial.depthTest = false;
        PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial.depthWrite = false;
        return PointsMaterialFactory.drawingSketchReferencePointSelectedSpriteMaterial;
    }
    

    /**
     * 获取实体引用预览控制点材质。
     *
     * @returns 实体引用预览控制点精灵材质。
     */
    static getReferencePreviewControlPointMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointReferencePreviewSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointReferencePreviewSpriteMaterial;
        }
        const material = PointsMaterialFactory.createControlPointStateSpriteMaterial(
            PointSpriteMaterialName.SketchControlPointReferencePreview,
            REFERENCE_PREVIEW_POINT_COLOR,
        );
        material.depthTest = false;
        material.depthWrite = false;
        PointsMaterialFactory.sketchControlPointReferencePreviewSpriteMaterial = material;
        return PointsMaterialFactory.sketchControlPointReferencePreviewSpriteMaterial;
    }

    /**
     * 获取实体引用来源高亮控制点材质。
     *
     * @returns 实体引用来源高亮控制点精灵材质。
     */
    static getReferenceSourceHighlightControlPointMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.sketchControlPointReferenceSourceHighlightSpriteMaterial) {
            return PointsMaterialFactory.sketchControlPointReferenceSourceHighlightSpriteMaterial;
        }
        PointsMaterialFactory.sketchControlPointReferenceSourceHighlightSpriteMaterial = PointsMaterialFactory.createControlPointStateSpriteMaterial(
            PointSpriteMaterialName.SketchControlPointReferenceSourceHighlight,
            REFERENCE_SOURCE_HIGHLIGHT_POINT_COLOR,
        );
        return PointsMaterialFactory.sketchControlPointReferenceSourceHighlightSpriteMaterial;
    }

    /**
     * 获取基准点默认材质。
     * @returns 基准点默认精灵材质。
     */
    static getDatumPointDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.datumPointDefaultSpriteMaterial) {
            return PointsMaterialFactory.datumPointDefaultSpriteMaterial;
        }
        PointsMaterialFactory.datumPointDefaultSpriteMaterial = PointsMaterialFactory.createDatumPointDefaultSpriteMaterial();
        return PointsMaterialFactory.datumPointDefaultSpriteMaterial;
    }

    /**
     * 获取基准点悬停材质。
     * @returns 基准点悬停精灵材质。
     */
    static getDatumPointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.datumPointHoverSpriteMaterial) {
            return PointsMaterialFactory.datumPointHoverSpriteMaterial;
        }
        PointsMaterialFactory.datumPointHoverSpriteMaterial = PointsMaterialFactory.createDatumPointStateSpriteMaterial(
            PointSpriteMaterialName.DatumPointHover,
            DATUM_POINT_HOVER_COLOR,
        );
        return PointsMaterialFactory.datumPointHoverSpriteMaterial;
    }

    /**
     * 获取基准点选中材质。
     * @returns 基准点选中精灵材质。
     */
    static getDatumPointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.datumPointSelectedSpriteMaterial) {
            return PointsMaterialFactory.datumPointSelectedSpriteMaterial;
        }
        PointsMaterialFactory.datumPointSelectedSpriteMaterial = PointsMaterialFactory.createDatumPointStateSpriteMaterial(
            PointSpriteMaterialName.DatumPointSelected,
            DATUM_POINT_SELECTED_COLOR,
        );
        return PointsMaterialFactory.datumPointSelectedSpriteMaterial;
    }

    /**
     * 获取特征点默认材质。
     * @returns 特征点默认精灵材质。
     */
    static getFeaturePointDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.featurePointDefaultSpriteMaterial) {
            return PointsMaterialFactory.featurePointDefaultSpriteMaterial;
        }
        PointsMaterialFactory.featurePointDefaultSpriteMaterial = PointsMaterialFactory.createFeaturePointSpriteMaterial(
            '特征点-默认态材质',
            FEATURE_POINT_COLOR_DEFAULT,
            0,
        );
        return PointsMaterialFactory.featurePointDefaultSpriteMaterial;
    }

    /**
     * 获取特征点悬停材质。
     * @returns 特征点悬停精灵材质。
     */
    static getFeaturePointHoverSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.featurePointHoverSpriteMaterial) {
            return PointsMaterialFactory.featurePointHoverSpriteMaterial;
        }
        PointsMaterialFactory.featurePointHoverSpriteMaterial = PointsMaterialFactory.createFeaturePointSpriteMaterial(
            '特征点-悬停态材质',
            FEATURE_POINT_COLOR_HOVER,
            1,
        );
        return PointsMaterialFactory.featurePointHoverSpriteMaterial;
    }

    /**
     * 获取特征点选中材质。
     * @returns 特征点选中精灵材质。
     */
    static getFeaturePointSelectedSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.featurePointSelectedSpriteMaterial) {
            return PointsMaterialFactory.featurePointSelectedSpriteMaterial;
        }
        PointsMaterialFactory.featurePointSelectedSpriteMaterial = PointsMaterialFactory.createFeaturePointSpriteMaterial(
            '特征点-选中态材质',
            FEATURE_POINT_COLOR_SELECTED,
            1,
        );
        return PointsMaterialFactory.featurePointSelectedSpriteMaterial;
    }

    /**
     * 为单个拓扑点创建独占状态材质，缓存 getter 仍只提供共享配方。
     *
     * 节点材质不能依赖 clone 复制圆形遮罩或相机订阅；必须从同一配方重新创建，
     * 并由 FeaturePoint 在销毁时释放全部已创建状态，而非只释放当前 material。
     * @param state 需要创建的视觉状态。
     * @param isPerspectiveCamera 已有点材质的当前投影状态；首次创建使用默认模板的状态。
     * @returns 由调用者独占并负责释放的节点材质。
     */
    static createOwnedFeaturePointSpriteMaterial(
        state: FeaturePointMaterialState,
        isPerspectiveCamera = PointsMaterialFactory.getFeaturePointDefaultSpriteMaterial().isPerspectiveCameraUniform.value,
    ): CameraSpriteNodeMaterial {
        const template = state === 'selected'
            ? PointsMaterialFactory.getFeaturePointSelectedSpriteMaterial()
            : state === 'hover'
                ? PointsMaterialFactory.getFeaturePointHoverSpriteMaterial()
                : PointsMaterialFactory.getFeaturePointDefaultSpriteMaterial();
        const material = PointsMaterialFactory.createFeaturePointSpriteMaterial(template.name, template.color, template.opacity);
        // 新建状态不能等待下一次相机事件，否则首次 Hover 可能按错误投影计算屏幕尺寸。
        material.isPerspectiveCameraUniform.value = isPerspectiveCamera;
        return material;
    }

    
    /**
     * 获取测量锚点精灵材质。
     *
     * @returns 测量锚点精灵材质。
     */
    static getMeasureAnchorSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.measureAnchorSpriteMaterial) {
            return PointsMaterialFactory.measureAnchorSpriteMaterial;
        }
        PointsMaterialFactory.measureAnchorSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            MEASURE_ANCHOR_COLOR,
        );
        PointsMaterialFactory.measureAnchorSpriteMaterial.name = PointSpriteMaterialName.MeasureAnchor;
        return PointsMaterialFactory.measureAnchorSpriteMaterial;
    }

    
    /**
     * 获取测量选中点精灵材质。
     *
     * 设计说明：
     * 1. 复用统一点精灵规范（圆形遮罩 + 固定屏幕像素尺寸）；
     * 2. 颜色与测量面/线选中绿色保持一致，避免测量模式点选中退化为方形精灵。
     *
     * @returns 测量选中点精灵材质。
     */
    static getMeasureSelectedPointSpriteMaterial(): CameraSpriteNodeMaterial {
        if (PointsMaterialFactory.measureSelectedPointSpriteMaterial) {
            return PointsMaterialFactory.measureSelectedPointSpriteMaterial;
        }
        PointsMaterialFactory.measureSelectedPointSpriteMaterial = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            MEASURE_SELECTED_COLOR,
        );
        PointsMaterialFactory.measureSelectedPointSpriteMaterial.opacity = 1;
        PointsMaterialFactory.measureSelectedPointSpriteMaterial.depthTest = false;
        PointsMaterialFactory.measureSelectedPointSpriteMaterial.depthWrite = false;
        PointsMaterialFactory.measureSelectedPointSpriteMaterial.name = PointSpriteMaterialName.MeasureSelectedPoint;
        return PointsMaterialFactory.measureSelectedPointSpriteMaterial;
    }
    
    
    /**
     * 获取草图点默认贴图（单例缓存）。
     * @returns 草图点默认贴图对象。
     */
    private static getSketchPointDefaultTexture(): THREE.Texture {
        if (PointsMaterialFactory.sketchPointDefaultTexture) {
            return PointsMaterialFactory.sketchPointDefaultTexture;
        }

        const pointTexture = PointsMaterialFactory.createPointTexture(
            POINT_DEFAULT_TEXTURE_URL,
            PointsMaterialFactory.createSketchPointDefaultPlaceholderImage(),
        );

        PointsMaterialFactory.sketchPointDefaultTexture = pointTexture;
        return PointsMaterialFactory.sketchPointDefaultTexture;
    }

    /**
     * 获取曲线中心点十字 SVG 纹理。
     *
     * 纹理与草图点雪花采用相同的本地 SVG 加载模式，并提供同步十字占位图，避免首帧退化成圆点。
     *
     * @returns 曲线中心点十字纹理单例。
     */
    private static getCurveCenterPointTexture(): THREE.Texture {
        if (PointsMaterialFactory.curveCenterPointTexture) {
            return PointsMaterialFactory.curveCenterPointTexture;
        }

        const centerPointTexture = PointsMaterialFactory.createPointTexture(
            CURVE_CENTER_POINT_TEXTURE_URL,
            PointsMaterialFactory.createCurveCenterPointPlaceholderImage(),
        );

        PointsMaterialFactory.curveCenterPointTexture = centerPointTexture;
        return PointsMaterialFactory.curveCenterPointTexture;
    }

    /**
     * 创建带同步占位图和异步 SVG 替换能力的点纹理。
     *
     * @param textureUrl 正式 SVG 纹理地址。
     * @param placeholderImage 首帧同步占位图。
     * @returns 已完成采样参数配置的点纹理。
     */
    private static createPointTexture(
        textureUrl: string,
        placeholderImage: HTMLCanvasElement,
    ): THREE.Texture {
        const pointTexture = new THREE.Texture();
        pointTexture.image = placeholderImage;
        pointTexture.colorSpace = THREE.SRGBColorSpace;
        pointTexture.minFilter = THREE.LinearFilter;
        pointTexture.magFilter = THREE.LinearFilter;
        pointTexture.wrapS = THREE.ClampToEdgeWrapping;
        pointTexture.wrapT = THREE.ClampToEdgeWrapping;
        pointTexture.needsUpdate = true;

        // 本地 SVG 完成加载后只替换纹理图像，所有状态材质继续共享同一个纹理对象。
        const imageLoader = new THREE.ImageLoader();
        imageLoader.load(textureUrl, (image) => {
            pointTexture.image = image;
            pointTexture.needsUpdate = true;
        });
        return pointTexture;
    }

    /**
     * 创建草图点默认贴图的同步占位图。
     * 说明：
     * 1. WebGPU 首帧会立即读取纹理图片对象，不能接受 `null` image；
     * 2. 这里使用同步可用的 canvas 作为占位图，待 SVG 异步加载完成后再覆盖；
     * 3. 占位图只负责兜底首帧稳定性，视觉上近似默认草图点即可。
     * @returns 可立即写入 Texture.image 的占位图。
     */
    private static createSketchPointDefaultPlaceholderImage(): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('创建草图点默认贴图占位图失败：2D 画布上下文不可用。');
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        // 占位图与正式 SVG 都使用白色遮罩，由材质节点负责着色，避免首帧颜色被重复相乘后变暗。
        context.fillStyle = new THREE.Color(SKETCH_POINT_TEXTURE_MASK_COLOR).getStyle();
        context.beginPath();
        context.arc(canvas.width / 2, canvas.height / 2, 8, 0, Math.PI * 2);
        context.fill();

        return canvas;
    }

    /**
     * 创建曲线中心点十字纹理的同步占位图。
     *
     * @returns 可在 SVG 完成加载前立即使用的白色十字画布。
     */
    private static createCurveCenterPointPlaceholderImage(): HTMLCanvasElement {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;

        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('创建曲线中心点十字贴图占位图失败：2D 画布上下文不可用。');
        }

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = new THREE.Color(SKETCH_POINT_TEXTURE_MASK_COLOR).getStyle();
        // 正式 SVG 围绕中心放大两倍后由 32×32 画布裁剪，首帧占位图必须呈现同样的满幅 10px 十字。
        context.fillRect(11, 0, 10, 32);
        context.fillRect(0, 11, 32, 10);
        return canvas;
    }
    


    /**
     * 创建统一基础配置的点精灵材质。
     * @param color 点颜色。
     * @returns 统一配置后的点精灵材质实例。
     */
    private static createUnifiedPointSpriteMaterial(
        color: THREE.ColorRepresentation,
    ): CameraSpriteNodeMaterial {
        // 按统一规范创建点材质：不随距离衰减、开启透明混合与圆形遮罩。
        const pointMaterial = new CameraSpriteNodeMaterial({
            sizeAttenuation: false,
            alphaToCoverage: true,
            transparent: true,
            opacityNode: shapeCircle(),
            color,
        });
        pointMaterial.transparent = true;
        pointMaterial.opacity = 0.5;
        return pointMaterial;
    }

    /**
     * 创建特征点精灵材质。
     *
     * 说明：
     * 1. 特征点用于拓扑顶点的选择反馈，悬停或选中时必须压在实体面片之上；
     * 2. 因此这里集中关闭深度测试与深度写入，避免高分辨率场景下交线端点被前景面遮挡而难以判断命中状态；
     * 3. 该方法只服务 FeaturePoint，避免扩大到草图点、控制点等仍需要按原有视觉规则渲染的对象。
     *
     * @param materialName 材质名称。
     * @param color 点颜色。
     * @param opacity 点透明度。
     * @returns 特征点精灵材质。
     */
    private static createFeaturePointSpriteMaterial(
        materialName: string,
        color: THREE.ColorRepresentation,
        opacity: number,
    ): CameraSpriteNodeMaterial {
        const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(color);
        // 特征点是选择提示对象，关闭深度测试可以保证其悬停/选中反馈不会被面片遮挡。
        material.depthTest = false;
        material.depthWrite = false;
        material.opacity = opacity;
        material.name = materialName;
        return material;
    }

    
    /**
     * 按点材质名称解析对应颜色。
     * @param materialName 点材质名称。
     * @returns 对应状态颜色。
     */
    private static resolveSketchPointColorByMaterialName(materialName: PointSpriteMaterialName): number {
        switch (materialName) {
            case PointSpriteMaterialName.SketchPointSelected:
                return SKETCH_POINT_SELECTED_COLOR;
            case PointSpriteMaterialName.SketchPointCreated:
                return SKETCH_POINT_CREATED_COLOR;
            case PointSpriteMaterialName.SketchPointDefault:
                return SKETCH_POINT_DEFAULT_COLOR;
            case PointSpriteMaterialName.SketchPointInactiveDefault:
                return SKETCH_POINT_DEFAULT_COLOR;
            case PointSpriteMaterialName.SketchPointFixedDefault:
                return SKETCH_FIXED_POINT_COLOR;
            case PointSpriteMaterialName.SketchPointFixedInactiveDefault:
                return SKETCH_FIXED_POINT_COLOR;
            case PointSpriteMaterialName.SketchPointHover:
                return SKETCH_POINT_HOVER_COLOR;
            case PointSpriteMaterialName.SketchControlPoint:
                return SKETCH_CONTROL_POINT_COLOR;
            case PointSpriteMaterialName.SketchControlPointInactive:
                return SKETCH_CONTROL_POINT_COLOR;
            case PointSpriteMaterialName.SketchControlPointFixed:
                return SKETCH_FIXED_POINT_COLOR;
            case PointSpriteMaterialName.SketchControlPointFixedInactive:
                return SKETCH_FIXED_POINT_COLOR;
            default:
                throw new Error(`未支持的点材质名称：${materialName}`);
        }
    }
    

    /**
     * 创建草图点精灵材质。
     * @param materialName 材质名称。
     * @returns 点精灵材质。
     */
    private static createSketchPointSpriteMaterial(
        materialName: PointSpriteMaterialName,
    ): CameraSpriteNodeMaterial {
        const materialColor = PointsMaterialFactory.resolveSketchPointColorByMaterialName(materialName);
        const pointTexture = PointsMaterialFactory.getSketchPointDefaultTexture();
        const pointTextureNode = texture(pointTexture);
        const material = new CameraSpriteNodeMaterial({
            sizeAttenuation: false,
            alphaToCoverage: true,
            transparent: true,
            color: materialColor,
            // 草图点所有交互状态复用同一张白色雪花遮罩，状态切换只改变颜色，避免悬停或选中时退化为圆形。
            colorNode: pointTextureNode.mul(
                vec4(color(materialColor), 1),
            ),
        });
        material.name = materialName;
        return material;
    }

    /**
     * 创建曲线中心点十字精灵材质。
     *
     * @param materialName 材质名称。
     * @param materialColor 材质着色颜色。
     * @param opacity 材质透明度。
     * @returns 复用中心点十字 SVG 遮罩的点精灵材质。
     */
    private static createCurveCenterPointSpriteMaterial(
        materialName: string,
        materialColor: THREE.ColorRepresentation,
        opacity: number = 1,
    ): CameraSpriteNodeMaterial {
        // TSL color() 不接受包含字符串的 ColorRepresentation 联合类型，先规范化为稳定的十六进制数值。
        const normalizedMaterialColor = new THREE.Color(materialColor).getHex();
        const centerPointTextureNode = texture(PointsMaterialFactory.getCurveCenterPointTexture());
        const material = new CameraSpriteNodeMaterial({
            sizeAttenuation: false,
            alphaToCoverage: true,
            transparent: true,
            color: normalizedMaterialColor,
            colorNode: centerPointTextureNode.mul(
                vec4(color(normalizedMaterialColor), 1),
            ),
        });
        material.name = materialName;
        material.opacity = opacity;
        return material;
    }

    /**
     * 创建基准点默认材质。
     * @returns 基准点默认精灵材质。
     */
    private static createDatumPointDefaultSpriteMaterial(): CameraSpriteNodeMaterial {
        // 基准点不使用 SVG 贴图，直接使用纯颜色圆点材质，避免贴图异步加载带来的首帧风险。
        const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            DATUM_POINT_DEFAULT_COLOR,
        );
        material.name = PointSpriteMaterialName.DatumPointDefault;
        return material;
    }


    /**
     * 创建控制点精灵材质。
     * @returns 控制点材质。
     */
    private static createControlPointSpriteMaterial(): CameraSpriteNodeMaterial {
        const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
            SKETCH_CONTROL_POINT_COLOR,
        );
        material.name = PointSpriteMaterialName.SketchControlPoint;
        return material;
    }

    /**
     * 创建控制点交互态材质（悬停/选中）。
     * @param materialName 材质名称。
     * @param color 交互态颜色。
     * @returns 控制点交互态材质。
     */
    private static createControlPointStateSpriteMaterial(
        materialName: PointSpriteMaterialName,
        color: THREE.ColorRepresentation,
    ): CameraSpriteNodeMaterial {
        const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(color);
        material.name = materialName;
        return material;
    }

    /**
     * 创建基准点交互态材质。
     * @param materialName 材质名称。
     * @param color 状态颜色。
     * @returns 基准点交互态材质。
     */
    private static createDatumPointStateSpriteMaterial(
        materialName: PointSpriteMaterialName,
        color: THREE.ColorRepresentation,
    ): CameraSpriteNodeMaterial {
        const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(color);
        material.name = materialName;
        return material;
    }
    


    /**
     * 创建拖拽留影点材质。
     * @param fallbackKind 留影点类型。
     * @returns 留影点材质。
     * @throws 当留影点类型不受支持时抛出错误。
     */
    static createDragSnapshotPointMaterial(
        fallbackKind: SketchPointCloneMaterialFallbackKind,
    ): CameraSpriteNodeMaterial {
        switch (fallbackKind) {
            case SketchPointCloneMaterialFallbackKind.SketchPoint: {
                // TSL 节点材质不能直接 clone；重新创建材质并复用缓存雪花纹理，保证普通草图点留影保持原有形状。
                const material = PointsMaterialFactory.createSketchPointSpriteMaterial(
                    PointSpriteMaterialName.SketchPointDefault,
                );
                material.name = '拖拽留影-草图点-默认材质';
                return material;
            }
            case SketchPointCloneMaterialFallbackKind.ControlPoint: {
                // 控制点继续使用代码生成的圆形遮罩，与普通草图点雪花样式保持区分。
                const material = PointsMaterialFactory.createUnifiedPointSpriteMaterial(
                    SKETCH_CONTROL_POINT_COLOR,
                );
                material.name = '拖拽留影-控制点-默认材质';
                return material;
            }
            default:
                throw new Error(`未支持的拖拽留影点类型：${fallbackKind as string}`);
        }
    }

    /**
     * 创建曲线中心点拖动留影材质。
     *
     * TSL 节点材质不能直接克隆，因此为当前留影创建独占材质，同时复用缓存的中心十字 SVG 纹理。
     *
     * @returns 曲线中心点拖动留影十字材质。
     */
    static createCurveCenterPointDragSnapshotMaterial(): CameraSpriteNodeMaterial {
        return PointsMaterialFactory.createCurveCenterPointSpriteMaterial(
            '拖拽留影-曲线中心点-十字材质',
            CURVE_CENTER_POINT_DEFAULT_COLOR,
        );
    }

}
